import React, { useEffect, useMemo, useState } from "react";
import "../styles/shared-ui.css";
import "../styles/job-detail.css";
import { useAppContext } from "../context/AppContext";
import { getTaskWithRelations } from "../services/taskService";
import RichTextEditor from "../components/ui/RichTextEditor";
import SideTabs from "../components/layout/SideTabs";
import SkillTable from "../components/ui/SkillTable";
import RaciMatrix from "../components/ui/RaciMatrix";
import { LEVELS } from "../components/ui/levels";
import { toast } from "../utils/toast";
import { useLocation, useParams } from "react-router-dom";

const describeRecurrence = (occurrences, frequencyInterval, frequencyPeriod) => {
  const occ = Number(occurrences);
  const interval = Number(frequencyInterval);
  if (
    !Number.isFinite(occ) ||
    !Number.isFinite(interval) ||
    occ <= 0 ||
    interval <= 0
  ) {
    return "";
  }
  const periodLabel = String(frequencyPeriod || "WEEK").toLowerCase();
  const periodText = interval === 1 ? periodLabel : `${periodLabel}s`;
  const timesText = occ === 1 ? "time" : "times";
  return `${occ} ${timesText} in ${interval} ${periodText}`;
};

const TaskDetailPage = () => {
  const { setActiveSection, setJobId } = useAppContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { taskId: taskIdParam } = useParams();
  const location = useLocation();
  const routeTaskId = useMemo(() => {
    if (taskIdParam != null && taskIdParam !== "") {
      try {
        return decodeURIComponent(taskIdParam);
      } catch {
        return taskIdParam;
      }
    }
    const pathname = String(location?.pathname || "");
    const match = pathname.match(/^\/tasks\/([^/]+)$/i);
    if (!match?.[1]) return null;
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  }, [taskIdParam, location?.pathname]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        let id = routeTaskId;
        if (routeTaskId) {
          try {
            localStorage.setItem("activeTaskId", String(id));
          } catch {}
        }
        if (!id) {
          try {
            const stored = localStorage.getItem("activeTaskId");
            if (stored) id = stored;
          } catch {}
        }
        const res = id ? await getTaskWithRelations(id) : null;
        setData(res);
      } catch (e) {
        toast.error(e?.message || "Failed to load task");
      } finally {
        setLoading(false);
      }
    })();
  }, [routeTaskId]);

  const goBack = () => {
    try {
      localStorage.removeItem("activeTaskId");
    } catch {}
    let returnTo = null;
    let returnJobId = null;
    try {
      returnTo = localStorage.getItem("taskReturnTo");
      returnJobId = localStorage.getItem("taskReturnJobId");
      localStorage.removeItem("taskReturnTo");
      localStorage.removeItem("taskReturnJobId");
      localStorage.removeItem("jobDetailsActiveTab");
    } catch {}
    if (returnTo === "job-detail" && returnJobId) {
      try {
        localStorage.setItem("activeJobId", returnJobId);
      } catch {}
      try {
        setJobId(returnJobId);
      } catch {}
      setActiveSection("job-detail", { jobId: returnJobId });
    } else if (returnTo === "job-workload") {
      setActiveSection("job-workload");
    } else {
      setActiveSection("task-management");
    }
  };

  const companyName = useMemo(() => data?.company?.name || "-", [data]);
  const recurrenceSummary = useMemo(
    () =>
      describeRecurrence(
        data?.occurrences,
        data?.frequency_interval,
        data?.frequency_period,
      ) || "-",
    [data],
  );
  const taskStatusLabel = useMemo(() => {
    return (
      data?.task_status?.description ||
      data?.status?.description ||
      data?.taskStatus?.description ||
      (data?.status_id ? `Status ${data.status_id}` : "-")
    );
  }, [data]);
  const taskCategoryLabel = useMemo(() => {
    return (
      data?.task_category?.description ||
      data?.category?.description ||
      data?.taskCategory?.description ||
      (data?.category_id ? `Category ${data.category_id}` : "-")
    );
  }, [data]);

  const processesList = useMemo(() => {
    // Normalize possible backend shapes to an array of { process_name, process_code, name, ... }
    if (!data) return [];
    // 1) Preferred: data.process (object or array)
    if (data.process) {
      const p = data.process;
      return Array.isArray(p) ? p : [p];
    }
    // 2) Alternative: data.processes (array)
    if (Array.isArray(data.processes)) {
      return data.processes;
    }
    // 3) Linking arrays: data.process_tasks or data.process_task with nested .process
    const links = Array.isArray(data.process_tasks)
      ? data.process_tasks
      : Array.isArray(data.process_task)
        ? data.process_task
        : [];
    if (links.length > 0) {
      return links.map((pt) => pt?.process || null).filter(Boolean);
    }
    // Fallback: no associated process info
    return [];
  }, [data]);

  const jobRows = useMemo(() => {
    const arr = Array.isArray(data?.jobTasks) ? data.jobTasks : [];
    return arr.map((jt) => jt.job).filter(Boolean);
  }, [data]);

  const raciRows = useMemo(() => {
    const arr = Array.isArray(data?.jobTasks) ? data.jobTasks : [];
    return arr
      .map((jt) => ({
        job_id: jt?.job_id ?? jt?.job?.job_id,
        role: jt?.role ?? null,
        time_allocation_percentage:
          jt?.time_allocation_percentage != null
            ? Number(jt.time_allocation_percentage)
            : null,
      }))
      .filter((r) => r.job_id != null);
  }, [data]);

  const defaultTab = React.useMemo(() => {
    try {
      const from = localStorage.getItem("taskReturnTo");
      if (from === "job-detail") {
        return localStorage.getItem("taskDetailsActiveTab") || "basic";
      }
      return "basic";
    } catch {
      return "basic";
    }
  }, []);

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Task Details</h2>
            <p className="page-subtitle">
              {data?.task_name
                ? `Viewing: ${data.task_name}`
                : "Read-only view of a task and its relations"}
              {data?.task_code ? ` (${data.task_code})` : ""}
            </p>
          </div>
          <div className="roles-toolbar">
            <button className="secondary-btn" onClick={goBack}>
              ← Back
            </button>
            <button
              className="primary-btn"
              onClick={() => {
                try {
                  localStorage.setItem("activeTaskId", String(data?.task_id));
                } catch {}
                setActiveSection("task-edit", { taskId: data?.task_id });
              }}
              disabled={!data?.task_id}
            >
              Edit
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        {loading ? (
          <div className="no-results">Loading...</div>
        ) : !data ? (
          <div className="no-results">Task not found</div>
        ) : (
          <SideTabs
            defaultActiveId={defaultTab}
            tabs={[
              {
                id: "basic",
                label: "Basic Details",
                content: (
                  <div className="role-card">
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Task Name</label>
                        <input value={data.task_name || ""} disabled />
                      </div>
                      <div className="form-group">
                        <label>Task Code</label>
                        <input value={data.task_code || ""} disabled />
                      </div>
                      <div className="form-group">
                        <label>Expected Process Time (min)</label>
                        <input
                          value={
                            data.expected_process_time != null
                              ? String(data.expected_process_time)
                              : ""
                          }
                          disabled
                        />
                      </div>
                      <div className="form-group">
                        <label>Expected Waiting Time (min)</label>
                        <input
                          value={
                            data.expected_waiting_time != null
                              ? String(data.expected_waiting_time)
                              : ""
                          }
                          disabled
                        />
                      </div>
                      <div className="form-group">
                        <label>Expected Rework Time (min)</label>
                        <input
                          value={
                            data.expected_rework_time != null
                              ? String(data.expected_rework_time)
                              : ""
                          }
                          disabled
                        />
                      </div>
                      <div className="form-group">
                        <label>Task Status</label>
                        <input value={taskStatusLabel} disabled />
                      </div>
                      <div className="form-group">
                        <label>Task Category</label>
                        <input value={taskCategoryLabel} disabled />
                      </div>
                      <div className="form-group">
                        <label>Company</label>
                        <input value={companyName} disabled />
                      </div>
                      <div className="form-group full">
                        <label>Recurrence</label>
                        <input value={recurrenceSummary} disabled />
                      </div>
                      <div className="form-group full">
                        <label>Associated Processes</label>
                        <div className="chip-list">
                          {processesList.length === 0 ? (
                            <span className="muted">None</span>
                          ) : (
                            processesList.map((p, idx) => {
                              const label =
                                p?.process_name ||
                                p?.name ||
                                `Process ${idx + 1}`;
                              const code = p?.process_code || p?.code;
                              return (
                                <span key={idx} className="chip">
                                  {label}
                                  {code ? ` [${code}]` : ""}
                                </span>
                              );
                            })
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                id: "description",
                label: "Description",
                content: (
                  <div className="role-card">
                    <RichTextEditor
                      label="Task Description"
                      value={data.task_overview || ""}
                      readOnly
                      height={280}
                    />
                  </div>
                ),
              },
              {
                id: "skills",
                label: "Skills",
                content: (
                  <div className="role-card">
                    <div
                      className="section-title"
                      style={{ marginBottom: 12, fontWeight: 600 }}
                    >
                      Req Skills
                    </div>
                    <SkillTable
                      mode="view"
                      skills={(Array.isArray(data.taskSkills)
                        ? data.taskSkills
                        : []
                      ).map((ts) => ({
                        name: ts?.skill?.name || ts?.skill_name || "-",
                        skillDescription: ts?.skill?.description || "",
                        level: ts?.level?.level_name || "",
                      }))}
                      levels={LEVELS}
                    />
                  </div>
                ),
              },
              {
                id: "raci",
                label: "RACI",
                content: (
                  <RaciMatrix mode="view" jobs={jobRows} rows={raciRows} label={data?.task_name} />
                ),
              },
            ]}
          />
        )}
      </div>
    </div>
  );
};

export default TaskDetailPage;
